'use client';

import {
	AlertTriangle,
	BadgeCheck,
	CheckCircle2,
	Clock3,
	Handshake,
	Plus,
	RefreshCw,
	ShieldCheck,
	UserCheck,
	WalletCards
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	AssignPartnerAdvisoryRequest,
	CreatePartnerAdvisoryRequest,
	PartnerAdvisoryCategory,
	PartnerAdvisoryCategoryInsight,
	PartnerAdvisoryChecklistItem,
	PartnerAdvisoryChecklistStatus,
	PartnerAdvisoryPriority,
	PartnerAdvisoryProgram,
	PartnerAdvisoryRequestResponse,
	PartnerAdvisoryRiskItem,
	PartnerAdvisoryRiskLevel,
	PartnerAdvisoryRiskSeverity,
	PartnerAdvisoryStatus,
	PartnerAdvisorySummaryResponse,
	ResolvePartnerAdvisoryRequest
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type PartnerAdvisoryFormState = {
	category: PartnerAdvisoryCategory;
	priority: PartnerAdvisoryPriority;
	riskLevel: PartnerAdvisoryRiskLevel;
	title: string;
	description: string;
	requesterEmployeeId: string;
	dueDate: string;
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function defaultDueDate(): string {
	const date = new Date();
	date.setDate(date.getDate() + 7);
	return date.toISOString().slice(0, 10);
}

function categoryLabel(category: PartnerAdvisoryCategory): string {
	const labels: Record<PartnerAdvisoryCategory, string> = {
		HR_STRATEGY: 'HR 전략',
		PAYROLL: 'Payroll',
		COMPLIANCE: '컴플라이언스',
		COMPENSATION: '평가·보상',
		EMPLOYEE_RELATIONS: '구성원 이슈',
		IMPLEMENTATION: '내재화'
	};
	return labels[category] ?? category;
}

function statusLabel(status: PartnerAdvisoryStatus): string {
	const labels: Record<PartnerAdvisoryStatus, string> = {
		NEW: '신규',
		TRIAGED: '분류',
		IN_PROGRESS: '진행중',
		RESOLVED: '해결',
		CANCELLED: '취소'
	};
	return labels[status] ?? status;
}

function priorityLabel(priority: PartnerAdvisoryPriority): string {
	const labels: Record<PartnerAdvisoryPriority, string> = {
		LOW: '낮음',
		MEDIUM: '보통',
		HIGH: '높음',
		URGENT: '긴급'
	};
	return labels[priority] ?? priority;
}

function statusVariant(status: PartnerAdvisoryStatus): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'RESOLVED') return 'success';
	if (status === 'NEW' || status === 'TRIAGED') return 'warning';
	if (status === 'CANCELLED') return 'destructive';
	return 'outline';
}

function checklistStatusLabel(status: PartnerAdvisoryChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: PartnerAdvisoryChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(
	severity: PartnerAdvisoryRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: PartnerAdvisoryChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="파트너 자문 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: PartnerAdvisoryRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 파트너 자문 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">파트너 리스크 큐</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function CategoryInsightsTable({ insights }: { insights: PartnerAdvisoryCategoryInsight[] }) {
	if (insights.length === 0) return <EmptyState message="카테고리별 자문 현황이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[560px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">카테고리</th>
						<th className="px-4 py-3 font-medium">진행</th>
						<th className="px-4 py-3 font-medium">해결</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{insights.map((insight) => (
						<tr key={insight.category} className="border-t border-border">
							<td className="px-4 py-4">
								<p className="font-semibold">{insight.label}</p>
								<p className="mt-1 text-xs text-muted-foreground">{categoryLabel(insight.category)}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{numberLabel(insight.openCount)}건</td>
							<td className="px-4 py-4 text-muted-foreground">{numberLabel(insight.resolvedCount)}건</td>
							<td className="px-4 py-4">
								<Badge variant={insight.riskLabel === '즉시 대응' ? 'warning' : 'outline'}>{insight.riskLabel}</Badge>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function ProgramsGrid({ programs }: { programs: PartnerAdvisoryProgram[] }) {
	if (programs.length === 0) return <EmptyState message="연결된 파트너 프로그램이 없습니다." />;
	return (
		<div className="grid gap-3 lg:grid-cols-2">
			{programs.map((program) => (
				<div key={program.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="flex items-start gap-3">
						<div className="rounded-md bg-muted p-2 text-muted-foreground">
							<Handshake className="size-4" />
						</div>
						<div className="min-w-0">
							<p className="font-semibold">{program.label}</p>
							<p className="mt-1 text-sm text-muted-foreground">{program.description}</p>
							<div className="mt-3 flex flex-wrap gap-2">
								{program.scopeItems.map((scope) => (
									<Badge key={scope} variant="outline">
										{scope}
									</Badge>
								))}
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function RequestsTable({
	requests,
	actionId,
	onAssign,
	onResolve
}: {
	requests: PartnerAdvisoryRequestResponse[];
	actionId: number | null;
	onAssign: (request: PartnerAdvisoryRequestResponse) => void;
	onResolve: (request: PartnerAdvisoryRequestResponse) => void;
}) {
	if (requests.length === 0) return <EmptyState message="등록된 파트너 자문 요청이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">요청</th>
						<th className="px-4 py-3 font-medium">담당</th>
						<th className="px-4 py-3 font-medium">기한</th>
						<th className="px-4 py-3 font-medium">상태</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{requests.map((request) => (
						<tr key={request.id} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{request.title}</p>
									<Badge variant={request.priority === 'URGENT' ? 'destructive' : 'outline'}>
										{priorityLabel(request.priority)}
									</Badge>
								</div>
								<p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{request.description}</p>
								<p className="mt-2 text-xs text-muted-foreground">
									{categoryLabel(request.category)} · 요청자 #{request.requesterEmployeeId}
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{request.partnerName ?? '미배정'}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(request.dueDate)}</td>
							<td className="px-4 py-4">
								<Badge variant={statusVariant(request.status)}>{statusLabel(request.status)}</Badge>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										size="sm"
										variant="outline"
										disabled={actionId === request.id || request.status === 'RESOLVED'}
										onClick={() => onAssign(request)}
									>
										<UserCheck className="size-4" />
										배정
									</Button>
									<Button
										type="button"
										size="sm"
										disabled={actionId === request.id || request.status === 'RESOLVED'}
										onClick={() => onResolve(request)}
									>
										<BadgeCheck className="size-4" />
										해결
									</Button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function PartnerAdvisoryPage() {
	const [state, setState] = useState<RequestState<PartnerAdvisorySummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [requestActionId, setRequestActionId] = useState<number | null>(null);
	const [form, setForm] = useState<PartnerAdvisoryFormState>({
		category: 'PAYROLL',
		priority: 'HIGH',
		riskLevel: 'HIGH',
		title: '',
		description: '',
		requesterEmployeeId: '',
		dueDate: defaultDueDate()
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<PartnerAdvisorySummaryResponse>('/partners/advisory/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitRequest(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('request');
		setActionMessage(null);
		try {
			const request: CreatePartnerAdvisoryRequest = {
				category: form.category,
				priority: form.priority,
				riskLevel: form.riskLevel,
				title: form.title.trim(),
				description: form.description.trim(),
				requesterEmployeeId: Number(form.requesterEmployeeId),
				dueDate: form.dueDate || null
			};
			await api.post('/partners/advisory/requests', request);
			setForm((current) => ({
				...current,
				title: '',
				description: '',
				requesterEmployeeId: '',
				dueDate: defaultDueDate()
			}));
			setActionMessage('파트너 자문 요청을 등록했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function assignPartner(request: PartnerAdvisoryRequestResponse) {
		setRequestActionId(request.id);
		setActionMessage(null);
		try {
			const payload: AssignPartnerAdvisoryRequest = {
				partnerName: request.partnerName ?? (request.category === 'PAYROLL' ? 'Payroll Partner' : 'HR Partner'),
				dueDate: request.dueDate ?? defaultDueDate()
			};
			await api.patch(`/partners/advisory/requests/${request.id}/assign`, payload);
			setActionMessage('담당 파트너를 배정했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setRequestActionId(null);
		}
	}

	async function resolveRequest(request: PartnerAdvisoryRequestResponse) {
		setRequestActionId(request.id);
		setActionMessage(null);
		try {
			const payload: ResolvePartnerAdvisoryRequest = {
				resolutionSummary: `${request.title} 자문 결과를 운영 화면에서 반영했습니다.`
			};
			await api.patch(`/partners/advisory/requests/${request.id}/resolve`, payload);
			setActionMessage('파트너 자문 요청을 해결 처리했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setRequestActionId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="파트너 자문"
				description="HR Partners와 Payroll Partners 수준의 전문 자문 요청, 배정, 해결 현황을 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="진행 요청"
							value={data.openRequestCount}
							description={`전체 ${numberLabel(data.totalRequestCount)}건`}
							icon={Handshake}
						/>
						<StatCard
							title="긴급·초과"
							value={`${numberLabel(data.urgentRequestCount)} / ${numberLabel(data.overdueRequestCount)}`}
							description="긴급 요청 / 기한초과"
							icon={AlertTriangle}
						/>
						<StatCard
							title="Payroll"
							value={data.payrollPartnerRequestCount}
							description={`HR 자문 ${numberLabel(data.hrPartnerRequestCount)}건`}
							icon={WalletCards}
						/>
						<StatCard
							title="평균 해결"
							value={`${numberLabel(data.averageResolutionDays)}일`}
							description={`해결 ${numberLabel(data.resolvedRequestCount)}건`}
							icon={ShieldCheck}
						/>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>자문 요청 등록</CardTitle>
							<CardDescription>급여, 노무, 조직, 보상 이슈를 파트너 요청으로 남기고 기한을 관리합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<form className="grid gap-3 xl:grid-cols-[1fr_1fr_1fr_auto]" onSubmit={submitRequest}>
								<div className="grid gap-3 sm:grid-cols-2 xl:col-span-2">
									<div>
										<Label htmlFor="partner-category">카테고리</Label>
										<select
											id="partner-category"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.category}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													category: event.target.value as PartnerAdvisoryCategory
												}))
											}
										>
											<option value="PAYROLL">Payroll</option>
											<option value="HR_STRATEGY">HR 전략</option>
											<option value="COMPLIANCE">컴플라이언스</option>
											<option value="COMPENSATION">평가·보상</option>
											<option value="EMPLOYEE_RELATIONS">구성원 이슈</option>
											<option value="IMPLEMENTATION">내재화</option>
										</select>
									</div>
									<div>
										<Label htmlFor="partner-title">제목</Label>
										<Input
											id="partner-title"
											value={form.title}
											onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
											required
										/>
									</div>
									<div>
										<Label htmlFor="partner-priority">우선순위</Label>
										<select
											id="partner-priority"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.priority}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													priority: event.target.value as PartnerAdvisoryPriority
												}))
											}
										>
											<option value="LOW">낮음</option>
											<option value="MEDIUM">보통</option>
											<option value="HIGH">높음</option>
											<option value="URGENT">긴급</option>
										</select>
									</div>
									<div>
										<Label htmlFor="partner-risk">리스크</Label>
										<select
											id="partner-risk"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.riskLevel}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													riskLevel: event.target.value as PartnerAdvisoryRiskLevel
												}))
											}
										>
											<option value="LOW">낮음</option>
											<option value="MEDIUM">보통</option>
											<option value="HIGH">높음</option>
											<option value="CRITICAL">치명</option>
										</select>
									</div>
									<div>
										<Label htmlFor="partner-requester">요청자 직원 ID</Label>
										<Input
											id="partner-requester"
											type="number"
											value={form.requesterEmployeeId}
											onChange={(event) =>
												setForm((current) => ({ ...current, requesterEmployeeId: event.target.value }))
											}
											required
										/>
									</div>
									<div>
										<Label htmlFor="partner-due-date">희망기한</Label>
										<Input
											id="partner-due-date"
											type="date"
											value={form.dueDate}
											onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))}
										/>
									</div>
								</div>
								<div className="xl:col-span-1">
									<Label htmlFor="partner-description">상세 내용</Label>
									<textarea
										id="partner-description"
										className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-28 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
										value={form.description}
										onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
										required
									/>
								</div>
								<div className="flex items-end">
									<Button type="submit" className="w-full" disabled={operationLoading === 'request'}>
										<Plus className="size-4" />
										요청 등록
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>실행 체크리스트</CardTitle>
								<CardDescription>HR·Payroll 파트너 운영 체계가 준비됐는지 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>카테고리 현황</CardTitle>
								<CardDescription>자문 영역별 진행과 해결 상태를 비교합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<CategoryInsightsTable insights={data.categoryInsights} />
							</CardContent>
						</Card>
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>파트너 프로그램</CardTitle>
								<CardDescription>상용 서비스로 제공할 HR/Payroll 파트너 범위를 명확히 보여줍니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ProgramsGrid programs={data.partnerPrograms} />
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>최근 자문 요청</CardTitle>
							<CardDescription>파트너 배정과 해결 처리를 바로 실행합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<RequestsTable
								requests={data.recentRequests}
								actionId={requestActionId}
								onAssign={(request) => void assignPartner(request)}
								onResolve={(request) => void resolveRequest(request)}
							/>
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
